import { MasterCombinationWithRelations } from "@/api/master-combinations-types";
import { RoleName } from "@/api/users-types";
import { CreateMasterCombination } from "@/components/master-combinations/CreateMasterCombination";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { Link } from "@/components/ui/Link";
import { NoPermissions } from "@/components/ui/NoPermissions";
import { useHasRole } from "@/hooks/use-has-role";
import { useRowCount } from "@/hooks/use-row-count";
import { useMasterCombinations } from "@/queries/master-combinations/use-master-combinations";
import { useMasterCombinationsDatagridStore } from "@/stores/master-combinations-datagrid-store";
import {
  containsOnlyOperator,
  isOnlyOperator,
  modelOptions,
  storyTypeOptions,
  timestampColumns,
} from "@/utils/datagrid-utils";
import { prettyStoryType } from "@/utils/pretty";
import { Stack } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

const columns: GridColDef<MasterCombinationWithRelations>[] = [
  {
    field: "id",
    headerName: "ID",
    width: 120,
  },
  {
    field: "name",
    headerName: "Name",
    width: 180,
  },
  {
    field: "storyConfig",
    headerName: "Story Config",
    width: 180,
    filterOperators: isOnlyOperator,
    type: "singleSelect",
    valueOptions: storyTypeOptions,
    valueGetter: (_, row) => row.storyConfigRevision.storyConfig.storyType,
    renderCell: (params) => (
      <Link
        to={`/story-configs/${params.row.storyConfigRevision.storyConfig.id}/revisions/${params.row.storyConfigRevision.revision}`}
      >
        {`${prettyStoryType(params.row.storyConfigRevision.storyConfig.storyType)} (rev. ${params.row.storyConfigRevision.revision})`}
      </Link>
    ),
  },
  {
    field: "textPrompt",
    headerName: "Text Prompt",
    width: 180,
    valueGetter: (_, row) =>
      `${row.promptRevision.logicalPrompt.name} (rev. ${row.promptRevision.revision})`, // makes it filterable by prompt name and revision, but requires to limit filter operators to "contains" only
    filterOperators: containsOnlyOperator,
    renderCell: (params) => (
      <Link
        to={`/prompts/${params.row.promptRevision.logicalPrompt.id}/revisions/${params.row.promptRevision.revision}`}
      >
        {`${params.row.promptRevision.logicalPrompt.name} (rev. ${params.row.promptRevision.revision})`}
      </Link>
    ),
  },
  {
    field: "imagePrompt",
    headerName: "Image Prompt",
    width: 180,
    valueGetter: (_, row) =>
      row.imagePromptRevision
        ? `${row.imagePromptRevision.logicalPrompt.name} (rev. ${row.imagePromptRevision.revision})`
        : "", // makes it filterable by prompt name and revision, but requires to limit filter operators to "contains" only
    filterOperators: containsOnlyOperator,
    renderCell: (params) => {
      if (!params.row.imagePromptRevision) {
        return null;
      }
      return (
        <Link
          to={`/prompts/${params.row.imagePromptRevision.logicalPrompt.id}/revisions/${params.row.imagePromptRevision.revision}`}
        >
          {`${params.row.imagePromptRevision.logicalPrompt.name} (rev. ${params.row.imagePromptRevision.revision})`}
        </Link>
      );
    },
  },
  {
    field: "llmModel",
    headerName: "LLM Model",
    width: 180,
    type: "singleSelect",
    valueOptions: modelOptions,
  },
  ...timestampColumns<MasterCombinationWithRelations>(),
];

export function MasterCombinationListPage() {
  const canView = useHasRole([
    RoleName.promptEngineer,
    RoleName.episodeManager,
  ]);
  const canCreate = useHasRole(RoleName.promptEngineer);

  const {
    paginationModel,
    sortModel,
    filterModel,
    setPaginationModel,
    setSortModel,
    setFilterModel,
  } = useMasterCombinationsDatagridStore();

  const { data, isLoading, error } = useMasterCombinations(paginationModel);

  const rowCount = useRowCount(data);

  if (!canView) {
    return <NoPermissions />;
  }

  return (
    <Stack gap={2} mb={2}>
      <ApiErrorAlert error={error} />
      <DataGrid
        rowHeight={46}
        columnHeaderHeight={46}
        columns={columns}
        filterModel={filterModel}
        loading={isLoading}
        pageSizeOptions={[25, 50, 100]}
        paginationMode={"server"}
        paginationModel={paginationModel}
        rowCount={rowCount}
        rows={data?.items}
        sortModel={sortModel}
        onPaginationModelChange={setPaginationModel}
        onFilterModelChange={setFilterModel}
        onSortModelChange={setSortModel}
      />
      {canCreate && (
        <Stack justifyContent={"flex-end"} direction={"row"} gap={1}>
          <CreateMasterCombination />
        </Stack>
      )}
    </Stack>
  );
}
